import React, { useState, useEffect } from 'react';
import { AppState } from '@/types';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getApp } from 'firebase/app';
import { X, Loader2, CheckCircle, LifeBuoy } from 'lucide-react';

interface SupportTicketModalProps {
  isOpen: boolean;
  onClose: () => void;
  state: AppState;
}

type UiState = 'form' | 'submitting' | 'success';

export const SupportTicketModal: React.FC<SupportTicketModalProps> = ({
  isOpen,
  onClose,
  state,
}) => {
  const [uiState, setUiState] = useState<UiState>('form');
  const [issueType, setIssueType] = useState('');
  const [severity, setSeverity] = useState<'critical' | 'medium' | 'low'>('medium');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [ticketId, setTicketId] = useState<string | null>(null);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setUiState('form');
      setIssueType('');
      setSeverity('medium');
      setTitle('');
      setDescription('');
      setError(null);
      setTicketId(null);
    }
  }, [isOpen]);

  // Handle Escape key
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEsc);
    }
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const trimmedTitle = title.trim();
    const trimmedDesc = description.trim();

    // Validation
    if (!issueType) {
      setError('Please select an issue type.');
      return;
    }
    if (!trimmedTitle || trimmedTitle.length < 5 || trimmedTitle.length > 80) {
      setError('Title must be between 5 and 80 characters.');
      return;
    }
    if (!trimmedDesc || trimmedDesc.length < 20 || trimmedDesc.length > 2000) {
      setError('Description must be between 20 and 2000 characters.');
      return;
    }

    // Assemble User Context
    const currentHousehold = state.households?.find(
      (h) => h.id === state.currentHouseholdId
    );
    const userContext = {
      plan: state.currentUser?.subscription?.tier ?? 'FREE',
      householdId: state.currentHouseholdId ?? null,
      householdName: currentHousehold?.name ?? null,
      householdSize: currentHousehold?.memberIds?.length ?? 0,
      accountCreatedAt: state.currentUser?.createdAt ?? new Date().toISOString(),
      lastActiveAt: null,
      recentErrorCodes: [],
    };

    const payload = {
      action: 'create',
      issueType,
      severity,
      title: trimmedTitle,
      description: trimmedDesc,
      userContext,
      appVersion: (import.meta.env.VITE_APP_VERSION as string) ?? 'unknown',
      userAgent: navigator.userAgent.slice(0, 200),
    };

    try {
      setUiState('submitting');
      const functions = getFunctions(getApp());
      const apiSupportTickets = httpsCallable(functions, 'apiSupportTickets');
      const result = await apiSupportTickets(payload);
      const data = result.data as { ticketId: string };
      setTicketId(data.ticketId);
      setUiState('success');
    } catch (err: unknown) {
      console.error('Support ticket error:', err);
      setError(
        err instanceof Error ? err.message : 'Failed to submit ticket. Please try again.'
      );
      setUiState('form');
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal Card */}
      <div className="relative w-full max-w-lg bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 overflow-y-auto max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-200 dark:border-slate-800">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <LifeBuoy className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <h2 className="text-xl font-bold text-slate-900 dark:text-white">
              Submit a Support Ticket
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors rounded-full hover:bg-slate-100 dark:hover:bg-slate-800"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {error && uiState === 'form' && (
            <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl text-sm text-red-600 dark:text-red-400">
              {error}
            </div>
          )}

          {(uiState === 'form' || uiState === 'submitting') && (
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Issue Type */}
              <div>
                <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider mb-2">
                  Issue Type
                </label>
                <select
                  value={issueType}
                  onChange={(e) => setIssueType(e.target.value)}
                  className="w-full p-3 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select an issue type...</option>
                  <option value="bug">Bug Report</option>
                  <option value="feature_request">Feature Request</option>
                  <option value="account_issue">Account Issue</option>
                  <option value="billing">Billing</option>
                  <option value="other">Other</option>
                </select>
              </div>

              {/* Severity */}
              <div>
                <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider mb-2">
                  Severity
                </label>
                <div className="flex gap-2">
                  {(['critical', 'medium', 'low'] as const).map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setSeverity(s)}
                      className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
                        severity === s
                          ? 'bg-brand text-white'
                          : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700'
                      }`}
                    >
                      {s.charAt(0).toUpperCase() + s.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Title */}
              <div>
                <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider mb-2">
                  Title <span className="text-slate-400">({title.length}/80)</span>
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  maxLength={80}
                  placeholder="Brief summary of the issue"
                  className="w-full p-3 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider mb-2">
                  Description{' '}
                  <span className="text-slate-400">({description.length}/2000)</span>
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  maxLength={2000}
                  rows={5}
                  placeholder="Please provide detailed steps to reproduce the issue..."
                  className="w-full p-3 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                />
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={uiState === 'submitting'}
                className="w-full h-12 rounded-xl bg-brand hover:bg-brand-hover text-white font-bold text-sm disabled:opacity-50 flex items-center justify-center gap-2 transition-colors"
              >
                {uiState === 'submitting' ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  'Submit Ticket'
                )}
              </button>
            </form>
          )}

          {uiState === 'submitting' && (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="w-12 h-12 text-brand animate-spin mb-4" />
              <p className="text-slate-600 dark:text-slate-400">Submitting your request...</p>
            </div>
          )}

          {uiState === 'success' && (
            <div className="flex flex-col items-center text-center py-8">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mb-6">
                <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-500" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
                Ticket Submitted!
              </h3>
              <p className="text-slate-600 dark:text-slate-400 mb-8">
                Your ticket #{ticketId} has been received. We'll follow up via email.
              </p>
              <button
                onClick={onClose}
                className="w-full h-12 rounded-xl bg-brand hover:bg-brand-hover text-white font-bold text-sm transition-colors"
              >
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};